import DefaultImage from "@assets/tabla_default.webp";
import Image from "@elements/ResponsiveImage/Image";
import ShareSocialButton from "@elements/ShareSocial/ShareSocialButton";
import Date from "@elements/Typography/Date";
import Heading from "@elements/Typography/Heading";
import { colorConstants } from "@helper/sectionConstants";
import { formatDate } from "@helper/useDateFormat";
import { NoSSR } from "@sphtech/web2-core/components";
import {
  ProcessedAuthorContent,
  type RawImageField,
} from "@typings/Opensearch";
import { CSSProperties } from "react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";

export type ArticleCardElementProps = {
  hasParagraph?: boolean;
  hasTruncated?: boolean;
  hasDate?: boolean;
  hasSectionName?: boolean;
  hasDarkBg?: boolean;
  hasSocialShare?: boolean;
  hasShowAuthor?: boolean;
};

export type ArticleCardStyleProps = {
  cardWrapper?: string;
  contentWrapper?: string;
  paragraphStyle?: string;
  titleWrapper?: string;
  titleClass?: string;
  titleStyle?: CSSProperties | undefined;
  imageWrapperStyle?: string;
  imageStyle?: string;
  dateClass?: string;
  sectionNameClass?: string;
  bottomInfoWrapperStyle?: string;
};

export type ArticleCardProps = {
  linkTo: string;
  title: string;
  sectionName?: string;
  paragraph?: string;
  createdDate?: string;
  time?: string;
  displayWidth: number;
  displayHeight: number;
  imageField?: RawImageField;
  imageAlt?: string;
  urlPath?: string;
  overrideStyle?: ArticleCardStyleProps;
  dateEdited?: string;
  refMedium?: string;
  elements?: ArticleCardElementProps;
  shareIcon?: string;
  fetchPriority?: "high" | "low" | "auto";
  authors?: ProcessedAuthorContent[];
};

export default function ArticleCard({
  title,
  sectionName,
  paragraph,
  createdDate,
  displayWidth,
  displayHeight,
  imageField,
  imageAlt,
  linkTo,
  overrideStyle,
  refMedium,
  elements,
  shareIcon,
  fetchPriority = "auto",
  authors,
}: ArticleCardProps) {
  const {
    cardWrapper,
    contentWrapper,
    imageWrapperStyle,
    imageStyle,
    paragraphStyle,
    titleClass,
    titleStyle,
    titleWrapper,
    dateClass,
    sectionNameClass,
    bottomInfoWrapperStyle,
  } = overrideStyle || {};

  const {
    hasParagraph = true,
    hasTruncated,
    hasDate,
    hasSectionName,
    hasSocialShare,
    hasDarkBg,
    hasShowAuthor,
  } = elements || {};
  const socialIconList = ["whatsapp", "facebook", "copylink", "x"];

  const sectionColor =
    sectionName && sectionName in colorConstants
      ? hasDarkBg &&
        colorConstants[sectionName as keyof typeof colorConstants].color
          .darkBg !== ""
        ? colorConstants[sectionName as keyof typeof colorConstants].color
            .darkBg
        : colorConstants[sectionName as keyof typeof colorConstants].color
            .lightBg
      : "";

  if (refMedium) {
    linkTo = `${linkTo}?ref=${refMedium}`;
  }

  let trimmedPara = "";
  if (hasParagraph && hasTruncated && paragraph) {
    const maxLength = 200;
    trimmedPara =
      paragraph.length > maxLength
        ? paragraph.slice(0, maxLength) + "..."
        : paragraph;
  }

  return (
    <div
      className={twMerge("flex w-full gap-3 xmd:gap-5 xmd:py-5", cardWrapper)}
    >
      <Link
        reloadDocument
        to={linkTo}
        className={twMerge(
          "w-full flex-shrink-0 md:flex-grow-0",
          imageWrapperStyle,
        )}
      >
        <Image
          displayWidth={displayWidth}
          srcWidth={displayWidth}
          srcHeight={displayHeight}
          src={imageField?.url ?? DefaultImage}
          alt={imageAlt ?? title}
          className={twMerge("w-full", imageStyle)}
          fetchPriority={fetchPriority}
        />
      </Link>

      <div
        className={twMerge(
          "flex flex-col w-full gap-3 flex-grow",
          contentWrapper,
        )}
      >
        {hasSectionName && sectionName && (
          <div
            className={twMerge(
              "w-max p-1 font-bold leading-4.5 text-xs font-opensans text-grey-400",
              sectionNameClass,
            )}
            style={{ borderTop: `3px solid ${sectionColor}` }}
          >
            {sectionName}
          </div>
        )}

        <div className={twMerge("gap-sm", titleWrapper)}>
          <Link reloadDocument to={linkTo}>
            <Heading
              Element="h2"
              className={twMerge(
                "font-bold font-primary hover:underline text-red-100",
                titleClass,
              )}
              style={titleStyle}
            >
              {title}
            </Heading>
          </Link>
        </div>
        {hasParagraph && (
          <p
            className={twMerge(
              "font-regular font-primary text-base lg:text-2sm leading-300",
              paragraphStyle,
            )}
          >
            {hasTruncated ? trimmedPara : paragraph}
          </p>
        )}
        <div
          className={twMerge(
            "inline-flex justify-between",
            bottomInfoWrapperStyle,
          )}
        >
          {hasDate && createdDate && (
            <NoSSR>
              <Date className={dateClass}>
                {formatDate(createdDate, "d MMM y H:mm")}
              </Date>
            </NoSSR>
          )}
          {hasSocialShare && (
            <ShareSocialButton
              socialIconList={socialIconList}
              pagePath={linkTo}
              shareIcon={shareIcon}
            />
          )}
        </div>
        {hasShowAuthor && authors && authors.length > 0 && (
          <div>
            {authors.map(
              (author, index) =>
                author.urlPath && (
                  <Link
                    key={index}
                    to={author.urlPath}
                    target="_blank"
                    className="font-opensans text-sm leading-[150%] font-bold text-grey-500"
                  >
                    {author.name}
                    {index < authors.length - 1 && <br />}
                  </Link>
                ),
            )}
          </div>
        )}
      </div>
    </div>
  );
}
